// src/components/inputs/TaskTimePicker.jsx
import React, { forwardRef, useId, useMemo, useRef } from "react";
import Flatpickr from "react-flatpickr";
// import "flatpickr/dist/flatpickr.min.css"; // garanta que esse CSS está importado globalmente

/* utils iguais aos anteriores... (pad2, parseHHMM, toDateFromHHMM, etc) */
function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}
function pad2(n) {
  const x = Number.isFinite(+n) ? +n : 0;
  return String(clamp(x, 0, 59)).padStart(2, "0");
}
function parseHHMM(v) {
  if (!v) return { h: null, m: null, valid: false };
  const [hh = "00", mm = "00"] = String(v).split(":");
  const h = clamp(parseInt(hh, 10) || 0, 0, 23);
  const m = clamp(parseInt(mm, 10) || 0, 0, 59);
  return { h, m, valid: true };
}
function toDateFromHHMM(v) {
  const { h, m, valid } = parseHHMM(v);
  if (!valid) return null;
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d;
}
function toHHMMFromDate(d) {
  if (!(d instanceof Date)) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}
function minutesOf(v) {
  const { h, m, valid } = parseHHMM(v);
  return valid ? (h ?? 0) * 60 + (m ?? 0) : null;
}
function hhmmFromMinutes(t) {
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(clamp(h, 0, 23)).padStart(2, "0")}:${String(
    clamp(m, 0, 59)
  ).padStart(2, "0")}`;
}
function clampToRange(hhmm, min, max) {
  const t = minutesOf(hhmm);
  if (t == null) return hhmm;
  const tMin = min != null ? minutesOf(min) : null;
  const tMax = max != null ? minutesOf(max) : null;
  const clamped =
    tMin == null && tMax == null
      ? t
      : tMin == null
      ? Math.min(t, tMax)
      : tMax == null
      ? Math.max(t, tMin)
      : clamp(t, tMin, tMax);
  return hhmmFromMinutes(clamped);
}
function snapMinuteToStep(hhmm, step) {
  const t = minutesOf(hhmm);
  if (t == null) return hhmm;
  const snapped = Math.round(t / step) * step;
  return hhmmFromMinutes(clamp(snapped, 0, 23 * 60 + 59));
}

const TaskTimePicker = forwardRef(
  (
    {
      value,
      onChange,
      step = 5,
      disabled,
      readOnly = false,
      placeholder = "HH:MM",
      name,
      id,
      className = "",
      allowClear = true,
      label,
      required = false,
      error,
      helperText,
      size = "md",
      // ⬇️ novidades
      appendTo, // por padrão: document.body
      preferAbove = true, // força abrir acima se possível
      min, // "HH:MM"
      max, // "HH:MM"
      snapToStep = true,
      leftIcon = null,
    },
    forwardedRef
  ) => {
    const internalId = useId();
    const inputId = id || `time-${internalId}`;
    const helpId = `help-${inputId}`;
    const errId = `err-${inputId}`;
    const fpRef = useRef(null);

    const dateVal = useMemo(() => toDateFromHHMM(value), [value]);
    const sizePad =
      size === "sm"
        ? "px-2 py-1.5 text-sm"
        : size === "lg"
        ? "px-3.5 py-2.5 text-base"
        : "px-3 py-2.5 text-sm border-gray-300 rounded-lg";

    const normalizeAndEmit = (raw) => {
      if (!raw) {
        onChange?.("");
        return;
      }
      const [h = "00", m = "00"] = raw.split(":");
      let hhmm = `${pad2(Math.min(Math.max(+h || 0, 0), 23))}:${pad2(m)}`;
      if (min || max) hhmm = clampToRange(hhmm, min, max);
      if (snapToStep && step > 1) hhmm = snapMinuteToStep(hhmm, step);
      onChange?.(hhmm);
    };

    return (
      <div className={className}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {label} {required && <span className="text-red-600">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
              {leftIcon}
            </span>
          )}

          <Flatpickr
            ref={(node) => {
              fpRef.current = node;
              if (typeof forwardedRef === "function") forwardedRef(node);
              else if (forwardedRef) forwardedRef.current = node;
            }}
            value={dateVal}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true,
              minuteIncrement: step,
              allowInput: true,
              // ⬇️ chave para NÃO ficar por baixo do modal:
              static: false, // importante: popover fora do input wrapper
              appendTo: appendTo || document.body, // anexa no body
              // tenta abrir acima (flatpickr respeita quando há espaço)
              position: preferAbove ? "above" : "auto",
              // limites opcionais
              minTime: min || undefined,
              maxTime: max || undefined,
              // iOS/Android: mantém UI consistente
              disableMobile: true,
            }}
            onChange={(dates) => {
              const d = dates?.[0];
              normalizeAndEmit(d ? toHHMMFromDate(d) : "");
            }}
            onClose={(_, __, instance) => {
              const raw = instance?._input?.value || "";
              normalizeAndEmit(raw);
            }}
            onReady={(selectedDates, dateStr, instance) => {
              // garante z-index MUITO alto (acima do Flowbite Modal)
              if (instance?.calendarContainer) {
                instance.calendarContainer.style.zIndex = "2147483647";
              }
            }}
            render={({ defaultValue, value: _v, ...props }, ref) => (
              <input
                {...props}
                id={inputId}
                name={name}
                ref={ref}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder}
                aria-invalid={!!error}
                aria-describedby={
                  error ? errId : helperText ? helpId : undefined
                }
                className={[
                  "w-full rounded-md border outline-none bg-white text-gray-900 focus:ring-2",
                  leftIcon ? "pl-8" : "",
                  sizePad,
                  error
                    ? "border-red-300 focus:ring-red-200"
                    : "border-gray-200 focus:ring-gray-300",
                  disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "",
                ].join(" ")}
              />
            )}
          />

          {allowClear && value && !disabled && !readOnly && (
            <button
              type="button"
              onClick={() => onChange?.("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Limpar horário"
              title="Limpar horário"
            >
              ✕
            </button>
          )}
        </div>

        {helperText && !error && (
          <p id={helpId} className="mt-1 text-xs text-gray-500">
            {helperText}
          </p>
        )}
        {error && (
          <p id={errId} className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}

        <style jsx global>{`
          .flatpickr-calendar {
            border: 1px solid #e5e7eb; /* gray-200 */
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 4px 6px -2px rgba(0, 0, 0, 0.05);
            border-radius: 0.5rem; /* rounded-xl */
          }
          .flatpickr-time {
            margin: 6px 8px;
          }
          .flatpickr-time .numInputWrapper span.arrowUp:after,
          .flatpickr-time .numInputWrapper span.arrowDown:after {
            border-color: #141414; /* gray-400 */
          }
          .flatpickr-time input.flatpickr-hour,
          .flatpickr-time input.flatpickr-minute {
            font-size: 14px;
          }
          .flatpickr-time.hasSeconds {
            display: none;
          }
        `}</style>
      </div>
    );
  }
);

export default TaskTimePicker;
